import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Save, Trash2, Loader2, ChevronUp, ChevronDown, Download } from 'lucide-react';
import { api } from '@/api/client';

interface NdrStatus { id: number; name: string }

const DEFAULTS: NdrStatus[] = [
  { id: 1, name: 'Office/Institute/RESIDENCE Closed' },
  { id: 2, name: 'Customer NOT AVAILABLE' },
  { id: 3, name: 'Customer did not receive the OTP Code' },
  { id: 4, name: 'Customer Refuse to share the OTP Code' },
  { id: 5, name: 'Customer REFUSED TO ACCEPT Order' },
  { id: 6, name: 'Customer Cancelled Order' },
  { id: 7, name: 'Customer OUT OF STATION' },
  { id: 8, name: 'ADDRESS INCOMPLETE/ INCORRECT' },
  { id: 9, name: 'ADDRESS UNLOCATABLE; CANNOT DELIVER' },
  { id: 10, name: 'DELIVERY SCHEDULED FOR NEXT WORKING DAY' },
  { id: 11, name: 'DELIVERY AREA NOT ACCESSIBLE' },
  { id: 12, name: 'Appointment awaited from consignee' },
  { id: 13, name: 'Delayed. No Entry' },
  { id: 14, name: 'OUT OF DELIVERY AREA' },
  { id: 15, name: 'COD Amount not ready' },
  { id: 16, name: 'Customer Want Open Delivery' },
  { id: 17, name: 'Customer Self Collect' },
  { id: 18, name: 'Fake/Duplicate Order' },
];

export function NdrStatusesPanel() {
  const qc = useQueryClient();
  const [rows, setRows] = useState<NdrStatus[]>([]);
  const [dirty, setDirty] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'ndr_statuses'],
    queryFn: async () => {
      try { return (await api.get<{ items?: NdrStatus[] }>('/settings/ndr_statuses')).data ?? null; }
      catch { return null; }
    },
  });

  useEffect(() => {
    if (data && Array.isArray((data as any).items)) setRows((data as any).items);
    else if (data === null) setRows([]);
  }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.put('/settings/ndr_statuses', { items: rows })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings', 'ndr_statuses'] }); setDirty(false); toast.success('NDR statuses saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  const update = (idx: number, patch: Partial<NdrStatus>) => { setRows(arr => arr.map((r, i) => i === idx ? { ...r, ...patch } : r)); setDirty(true); };
  const remove = (idx: number) => { setRows(arr => arr.filter((_, i) => i !== idx)); setDirty(true); };
  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir; if (j < 0 || j >= rows.length) return;
    const next = [...rows]; [next[idx], next[j]] = [next[j], next[idx]]; setRows(next); setDirty(true);
  };
  const add = () => {
    const nextId = rows.length ? Math.max(...rows.map(r => r.id)) + 1 : 1;
    setRows(arr => [...arr, { id: nextId, name: '' }]); setDirty(true);
  };


  const doExport = () => {
    if (!rows.length) return;
    const headers = ['id','name'];
    const lines = [headers.join(','), ...rows.map(r => headers.map(h => {
      const v = (r as any)[h];
      const s = v === undefined || v === null ? '' : String(v);
      return s.includes(',') || s.includes('"') ? '"' + s.replace(/"/g,'""') + '"' : s;
    }).join(','))];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ndr-codes-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">NDR Reason Codes</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Why a delivery attempt failed. Couriers push these as integers (1, 2, 3…); this list maps them to human-readable reasons.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {rows.length === 0 && (
            <button onClick={() => { setRows(DEFAULTS); setDirty(true); }} className="px-3 py-2 text-sm text-primary underline">
              Load default 18 reasons
            </button>
          )}
          <button onClick={doExport} disabled={!rows.length} className="px-3 py-2 text-sm border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 flex items-center gap-2 disabled:opacity-50"><Download size={14} /> Export CSV</button>
          <button onClick={add} className="px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center gap-2">
            <Plus size={14} /> Add Reason
          </button>
          <button onClick={() => save.mutate()} disabled={!dirty || save.isPending}
            className="px-4 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </button>
        </div>
      </div>

      {rows.length === 0 && (
        <div className="border border-dashed border-slate-300 rounded-lg p-8 text-center text-sm text-slate-500">
          No NDR reasons defined yet. Click <strong>Load default 18 reasons</strong> to start with the standard list.
        </div>
      )}

      {rows.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 w-20">Sort</th>
                <th className="text-left px-3 py-2 w-16">ID</th>
                <th className="text-left px-3 py-2">Reason Name</th>
                <th className="px-3 py-2 w-12" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronUp size={14} /></button>
                      <button onClick={() => move(idx, 1)} disabled={idx === rows.length - 1} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronDown size={14} /></button>
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" value={r.id} onChange={(e) => update(idx, { id: Number(e.target.value) })}
                      className="w-14 px-1.5 py-1 border border-slate-300 rounded text-sm font-mono focus:border-primary outline-none" />
                  </td>
                  <td className="px-2 py-1">
                    <input value={r.name} onChange={(e) => update(idx, { name: e.target.value })}
                      placeholder="Reason text" className="w-full px-2 py-1 border border-slate-300 rounded text-sm focus:border-primary outline-none" />
                  </td>
                  <td className="px-2 py-1">
                    <button onClick={() => remove(idx)} className="p-1.5 text-slate-400 hover:text-danger hover:bg-danger/10 rounded"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
